import api from '../api'
import type {
  Campaign,
  CampaignApplicationsQueryParams,
  CampaignApplicationsResponse,
  CampaignsByUserQueryParams,
  CampaignsQueryParams,
  CampaignsResponse,
  CreateCampaignPayload,
  UpdateCampaignPayload,
} from './types'

export const getCampaigns = async (params?: CampaignsQueryParams): Promise<CampaignsResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.p) queryParams.append('p', String(params.p))
  if (params?.ps) queryParams.append('ps', String(params.ps))
  if (params?.name) queryParams.append('name', params.name)
  if (params?.status?.length) {
    for (const status of params.status) {
      queryParams.append('status', status)
    }
  }
  if (params?.industry?.length) {
    for (const ind of params.industry) {
      queryParams.append('industry', ind)
    }
  }
  if (params?.platform?.length) {
    for (const plat of params.platform) {
      queryParams.append('platform', plat)
    }
  }

  const response = await api.get<CampaignsResponse>(`/brands/campaigns?${queryParams.toString()}`)
  return response.data
}

export const getCampaignsByUser = async (id: string, params?: CampaignsByUserQueryParams): Promise<CampaignsResponse> => {
  const searchParams = new URLSearchParams()
  if (params?.p) {
    searchParams.append('p', params.p.toString())
  }

  if (params?.ps) {
    searchParams.append('ps', params.ps.toString())
  }

  if (params?.CampaignStatus) {
    searchParams.append('CampaignStatus', params.CampaignStatus)
  }

  const queryString = searchParams.toString()
  const url = queryString ? `/brands/influencers/${id}/campaigns?${queryString}` : `/brands/influencers/${id}/campaigns`
  const response = await api.get(url)
  return response.data
}

export const getCampaignById = async (id: string): Promise<Campaign> => {
  const response = await api.get(`/brands/campaigns/${id}`)
  return response.data
}

export const createCampaign = async (campaign: CreateCampaignPayload): Promise<Campaign> => {
  const response = await api.post('/brands/campaigns_s1', campaign)
  return response.data
}

export const updateCampaign = async (campaign: UpdateCampaignPayload): Promise<Campaign> => {
  const response = await api.put('/brands/campaigns_s2', campaign)
  return response.data
}

export const uploadCampaignCoverImage = async (campaignId: string, imageFile: File): Promise<void> => {
  const formData = new FormData()
  formData.append('file', imageFile)

  const response = await api.post(`/brands/campaigns/${campaignId}/cover_image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

export const uploadCampaignAssets = async (campaignId: string, files: File[]): Promise<void> => {
  // Create an array of upload promises, one for each file
  const uploadPromises = files.map((file) => {
    const formData = new FormData()
    formData.append('file', file)

    return api.post(`/brands/campaigns/${campaignId}/assets`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000,
    })
  })

  // Wait for all uploads to complete
  await Promise.all(uploadPromises)
}

export const getCampaignApplications = async (
  campaignId: string,
  params?: CampaignApplicationsQueryParams,
): Promise<CampaignApplicationsResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.p) queryParams.append('p', String(params.p))
  if (params?.ps) queryParams.append('ps', String(params.ps))

  const response = await api.get<CampaignApplicationsResponse>(`/brands/campaigns/${campaignId}/applications?${queryParams.toString()}`)
  return response.data
}

export const stopCampaign = async (campaignId: string): Promise<void> => {
  const response = await api.put(`/brands/campaigns/${campaignId}/stop`)
  return response.data
}
